import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';

import Input from 'components/Input';
import Button from 'components/Button';
import FormNavigation from 'components/FormNavigation';
import SelectableCard from 'components/Common/SelectableCard';
import FormContainer from 'components/LoanForm/FormContainer';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getApplicationData } from 'selectors/getApplicationData';
import { FlowComponentType } from 'routes/types';
import { sendDirectDepositEnrollmentFormEmail } from 'thunks';

import { EMAIL_PATTERN } from 'components/FinancialCheckup/Email/YourEmail';
import { getMessageForInvalidFields, getMessageForRequiredFields } from 'utils/errors';

import { WhatIsTheIssueResult } from 'enums/FlowNextResults';

import styles from './NotifyYourTeam.module.scss';

enum EmailInput {
  Label = 'Email',
  Name = 'email',
}

enum NotifyYourTeamResult {
  SendEmailToCustomer = 'SendEmailToCustomer',
  SendEmailToYourTeam = 'SendEmailToYourTeam',
}

const NotifyYourTeam = ({ navigationInfo, handleNext }: FlowComponentType) => {
  const location = useLocation();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const { application, isLoading } = useSelector(getApplicationData);
  const { id: applicationId, manualDeductionEmailSent } = application!;
  const { errorOptionSelected } = (location.state || {}) as { errorOptionSelected?: WhatIsTheIssueResult };

  const [optionSelected, setOptionSelected] = useState<NotifyYourTeamResult>();
  const sendEmailToYourTeamSelected = optionSelected === NotifyYourTeamResult.SendEmailToYourTeam;

  const {
    register,
    watch,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm({
    mode: 'onBlur',
  });

  const watcher = watch();

  useEffect(() => {
    register(EmailInput.Name, {
      required: getMessageForRequiredFields(EmailInput.Label),
      pattern: {
        message: getMessageForInvalidFields(EmailInput.Label),
        value: EMAIL_PATTERN,
      },
    });
  }, [register]);

  const handleSendEmail = async () => {
    setOptionSelected(NotifyYourTeamResult.SendEmailToCustomer);
    dispatchWithUnwrap(sendDirectDepositEnrollmentFormEmail({ applicationId }));
    handleNext();
  };

  const handleSendEmailToYourTeam = async () => {
    dispatchWithUnwrap(
      sendDirectDepositEnrollmentFormEmail({
        applicationId,
        hrEmail: watcher[EmailInput.Name],
        errorOptionSelected,
      }),
    );
    handleNext();
  };

  return (
    <>
      <FormNavigation
        {...navigationInfo}
        {...(sendEmailToYourTeamSelected && {
          onBackClick: () => setOptionSelected(undefined),
        })}
      />
      <FormContainer
        title={
          sendEmailToYourTeamSelected
            ? 'What’s the email address for your payroll team?'
            : 'No problem — let’s notify your team'
        }
      >
        {sendEmailToYourTeamSelected ? (
          <>
            <Input
              autoFocus
              value={watcher[EmailInput.Name]}
              autoCapitalize="none"
              className={styles.formInput}
              onChange={(event) => {
                setValue(EmailInput.Name, event.target.value.trim());
                trigger(EmailInput.Name);
              }}
              label={EmailInput.Label}
              placeholder="youremail@mail.com"
              errorMessage={errors[EmailInput.Name]?.message}
              autoComplete="email"
              onKeyUp={(e) => e.key === 'Enter' && isValid && handleSendEmailToYourTeam()}
              disabled={isLoading}
            />
            <Button disabled={!isValid} onClick={handleSendEmailToYourTeam} isLoading={isLoading}>
              Next
            </Button>
          </>
        ) : (
          <div className={styles.content}>
            {/* // TODO: We will hide this card for now */}
            {/* <SelectableCard
              withRadio={false}
              disabled={isLoading}
              onClick={() => setOptionSelected(NotifyYourTeamResult.SendEmailToYourTeam)}
              title="Send an email to your team now."
              subtitle="You’ll enter their address."
            /> */}
            <SelectableCard
              isLoading={isLoading && optionSelected === NotifyYourTeamResult.SendEmailToCustomer}
              disabled={isLoading || manualDeductionEmailSent}
              withRadio={false}
              onClick={handleSendEmail}
              title="Get the email sent to you."
              subtitle="You’ll forward it."
            />
          </div>
        )}
      </FormContainer>
    </>
  );
};

export default NotifyYourTeam;
